import { NextPage, GetStaticPaths, GetStaticPropsContext } from 'next'
import { Fragment, useEffect, useState } from 'react'

import { useRouter } from 'next/router'

import { getBootcampsList, getSubjectsListWithCourses } from '~/../utils/data-fetch/bootcamp/bootcamp.query'
import Metatags from '~/components/Metadata/Metatags'

import { DEFAULT_LAYOUT_TRANSLATION_MODULE_NAMES } from '~/shared/layouts/constants'
import { BootCampItemGroq, bootcampOverviewGroq } from '~/types/bootcamp'
import { AppLanguage } from '~/types/locale'

import { usePageTitle } from '~/utils/tags'
import { GetProps } from '~/utils/pageWrapper/types'
import { getStaticPropsWrapper } from '~/utils/pageWrapper/staticPageWrapper'

import { DefaultLayout } from '~/shared/layouts/DefaultLayout'
import createBootcampsList from '~/bootcamp/utils/createBootcampsList.utils'

import BootcampVideoTopic from '~/bootcamp/pages/BootcampSubjectsList/BootcampVideoTopic'
import { useGetBootcampSubjectWithCoursesQuery } from '~/bootcamp/apis/get-subject-with-course.api'
import getSubjectsWithCoursesList from '~/bootcamp/utils/getSubjectsWithCoursesList'
import { isVideoTopicNotFound } from '~/bootcamp/pages/BootcampView/BootcampView.utils'
import { CategoryPathUrls } from '~/bootcamp/types/bootcamp.types'
import { CATEGORY_PATHS } from '~/bootcamp/constants/bootcamp.const'

const BootcampListNextPage: NextPage<{
  listData: bootcampOverviewGroq
  subjectList: BootCampItemGroq[]
  noindex: boolean
}> = ({ listData, subjectList, noindex }) => {
  const router = useRouter()
  const { query, locale } = router
  const { key, subtopic } = query
  const [subjectsWithCourses, setSubjectsWithCourses] = useState<BootCampItemGroq[]>(subjectList)

  const { data: subjectsListData } = useGetBootcampSubjectWithCoursesQuery(
    { language: locale as AppLanguage, gymCategory: subtopic as string },
    { skip: !subtopic },
  )

  const { data: enSubjectsWithCoursesData } = useGetBootcampSubjectWithCoursesQuery(
    { language: AppLanguage.EN, gymCategory: subtopic as string },
    { skip: !subtopic || locale === AppLanguage.EN },
  )

  useEffect(() => {
    if (subjectsListData?.length && enSubjectsWithCoursesData?.length) {
      const combinedSubjectList = getSubjectsWithCoursesList(subjectsListData, enSubjectsWithCoursesData)
      setSubjectsWithCourses(combinedSubjectList)
    } else {
      setSubjectsWithCourses(subjectList)
    }
  }, [subjectsListData, subjectList, enSubjectsWithCoursesData])

  const pageTitle = usePageTitle(subjectsWithCourses?.find((sub) => sub.key === key)?.name || '')

  return (
    <Fragment>
      <Metatags metaDescription={listData.metaDescription} noindex={noindex} title={pageTitle} />

      <DefaultLayout topbarClassName="not-mobile">
        <BootcampVideoTopic subjectList={subjectsWithCourses} />
      </DefaultLayout>
    </Fragment>
  )
}

export const getStaticPaths: GetStaticPaths = () => {
  return {
    paths: [],
    fallback: 'blocking',
  }
}

export const getProps: GetProps =
  () =>
  async ({ locale, params }: GetStaticPropsContext<{ subtopic: string; key: string }>) => {
    const subtopic = params?.subtopic as CategoryPathUrls

    if (isVideoTopicNotFound(subtopic)) {
      return {
        notFound: true,
      }
    }
    const [bootcampsListSanityData, { subjects }, subjectsListEnData] = await Promise.all([
      getBootcampsList(locale as AppLanguage, params?.key),
      getSubjectsListWithCourses(locale as AppLanguage, params ? (params.subtopic as string) : ''),
      locale !== AppLanguage.EN ? getSubjectsListWithCourses(AppLanguage.EN, subtopic ?? '') : undefined,
    ])

    if (!bootcampsListSanityData.reference) {
      return {
        notFound: true,
      }
    }
    const bootcampsListData = createBootcampsList(bootcampsListSanityData)

    // we are getting the english subjects and corresponding course list here to combine with the current locale subjects and courses list. This is to show the available in english tag on the bootcamp subjects and courses list, if the current locale is not english and the subjects and courses is available in english.

    let combinedSubjects: BootCampItemGroq[] = []
    if (subjectsListEnData) {
      const { subjects: subjectsEn } = subjectsListEnData
      combinedSubjects = getSubjectsWithCoursesList(subjects, subjectsEn)
    }

    return {
      props: {
        path: `/bootcamp/${subtopic}/${params?.key}`,
        namespaces: ['bootcamp', 'avatar', ...DEFAULT_LAYOUT_TRANSLATION_MODULE_NAMES],
        listData: bootcampsListData,
        subjectList: combinedSubjects.length ? combinedSubjects : subjects,
        noindex: subtopic === CATEGORY_PATHS.curriculum,
      },
      revalidate:
        process.env.NEXT_PUBLIC_INSTANT_SANITY_PREVIEW === 'true'
          ? 1
          : process.env.NEXT_PUBLIC_ENVIRONMENT === 'staging'
          ? 60 * 5
          : undefined,
    }
  }

export const getStaticProps = getStaticPropsWrapper(getProps)

export default BootcampListNextPage
